import { FormSection } from 'framework/ui/layout';
import { TextInput, DateInput, useFormContext, Checkbox, Dropdown, NumberInput } from 'framework/form';
import { useDateService } from 'framework/utils';
import {
  MilestoneScheduleItemResponse,
  ScheduleItem,
  ScheduleItemStatus,
} from 'domain/projects/newProject/finance/billing/billingTypes';
import { formatPeriod, formatCustomPeriod } from 'domain/projects/newProject/finance/billing/billingUtils';
import {
  BillingType,
  CHANGE_REQUEST_BILLING_OPTIONS_MILESTONE,
  CHANGE_REQUEST_BILLING_OPTIONS_PERIODIC,
  CHANGE_REQUEST_BILLING_TYPES,
} from 'domain/shared/newProject';
import { Grid2 as Grid, Stack } from '@mui/material';
import MDEditorWithPreview from '../mdEditor/MDEditorWithPreview';
import RadioButtonGroupExtended from './Create/RadioButtonGroupExtended';
import { OPDateIcon } from 'framework/ui/mui-builder';
import { Typography } from '@mui/material';

const getScheduleItemLabel = (item: any): string => {
  switch (item.type) {
    case ScheduleItem.MILESTONE:
      return `${item.alias} ${item.milestoneName}`;
    case ScheduleItem.CALENDAR_PERIOD:
      return formatPeriod(item.position, item.periodMonth);
    case ScheduleItem.CUSTOM_PERIOD:
      return formatCustomPeriod(item.startDate, item.endDate);
    default:
      return item.alias || item.id;
  }
};

const ChangeRequestForm = ({ projectId }: { projectId: string | undefined }) => {
  const { data = {} } = useFormContext();
  const { getToday } = useDateService();

  const isMilestone =
    data.billingType === BillingType.MILESTONE ||
    (data.billingType == null && data.milestones?.some((item: any) => item.type === ScheduleItem.MILESTONE));

  const billingOptions = isMilestone
    ? CHANGE_REQUEST_BILLING_OPTIONS_MILESTONE
    : CHANGE_REQUEST_BILLING_OPTIONS_PERIODIC;

  return (
    <>
      <FormSection>
        <Grid
          container
          spacing={1}
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Grid size={4}>
            <TextInput label="" source="title" placeholder="Change request title" />
          </Grid>
          <Grid size={2}>
            <NumberInput label="" source="additionalHours" placeholder="Added hours" />
          </Grid>
          <Grid size={2}>
            <NumberInput label="" source="additionalBudgetAmount" placeholder="Added budget $" />
          </Grid>
          <Grid size={2}>
            <NumberInput label="" source="additionalFte" placeholder="FTE people" />
          </Grid>
          <Grid size={2}>
            <DateInput
              label=""
              source="newDeliveryDate"
              placeholder="New budget end date"
              minDate={getToday()}
              helpText={data.newDeliveryDate ? 'The budget end date will be updated' : ''}
              sx={{
                '& .MuiFormHelperText-root': {
                  color: data.newDeliveryDate ? 'error.main' : 'text.secondary',
                },
              }}
            />
          </Grid>
        </Grid>
        <MDEditorWithPreview
          projectId={projectId}
          source="rawContent"
          label=""
          placeholder=""
          defaultPlaceholders={{
            receiverFullName: data.receiverFullName,
            receiverTitle: data.receiverTitle,
            alias: data.alias || '-',
            title: data.title,
            newDeliveryDate: data.newDeliveryDate,
            additionalBudgetAmount: data.additionalBudgetAmount,
            additionalFte: data.additionalFte,
            additionalHours: data.additionalHours,
            companyName: data.companyName,
          }}
        />

        <Grid container spacing={1}>
          <RadioButtonGroupExtended
            options={billingOptions}
            source="billingType"
            isColumnDirection={false}
            label="Change request shall be billed:"
          />
          {data.billingType === CHANGE_REQUEST_BILLING_TYPES.SPECIFIC_MILESTONE && (
            <Grid size={6}>
              <Dropdown
                options={
                  data.milestones?.map((item: any) => ({
                    value: item.id,
                    label: getScheduleItemLabel(item),
                  })) || []
                }
                source="billingMilestoneId"
                placeholder="Select billing item"
                label=""
              />
            </Grid>
          )}
        </Grid>
        {isMilestone && (
          <>
            <Checkbox source="milestoneSchedule" label="Affects milestone schedule" />
            {data.milestoneSchedule && (
              <Stack>
                {data.milestones
                  .filter((milestone: MilestoneScheduleItemResponse) => milestone.status === ScheduleItemStatus.PLANNED)
                  .map((milestone: MilestoneScheduleItemResponse) => {
                    const milestoneIndex = data.milestones.findIndex((item: any) => item.id === milestone.id);
                    return (
                      <Grid container direction="row" spacing={1} key={`milestone-${milestone.id}`}>
                        <Grid size={1}>
                          <TextInput
                            label=""
                            source={`milestones.${milestoneIndex}.alias`}
                            disabled
                            value={milestone.alias}
                          />
                        </Grid>
                        <Grid size={3.5}>
                          <TextInput
                            label=""
                            source={`milestones.${milestoneIndex}.milestoneName`}
                            disabled
                            value={milestone.milestoneName}
                          />
                        </Grid>
                        <Grid size={2}>
                          <DateInput
                            label=""
                            disabled={!!milestone.invoiceDate}
                            placeholder="YYY-MM-DD"
                            minDate={milestone.invoiceDate}
                            defaultValue={milestone.invoiceDate || undefined}
                            dateIcon={
                              <OPDateIcon
                                sx={{
                                  marginRight: '10px',
                                  fontSize: '1.2rem',
                                  position: 'absolute',
                                  zIndex: 1,
                                  left: '10px',
                                  top: '10px',
                                  opacity: '0.8',
                                }}
                              />
                            }
                            source={`milestones.${milestoneIndex}.invoiceDate`}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
              </Stack>
            )}
          </>
        )}

        <Stack spacing={1.5} mt={2}>
          <Typography>Client side signatory</Typography>
          <Grid container direction="row" spacing={1}>
            <Grid size={3}>
              <TextInput source="receiverFullName" label="" placeholder="Client responsible person (full name)" />
            </Grid>
            <Grid size={3}>
              <TextInput source="receiverTitle" label="" placeholder="Job title" />
            </Grid>
            <Grid size={3}>
              <TextInput source="receiverEmail" label="" placeholder="Client's email" />
            </Grid>
            <Grid size={3}>
              <TextInput source="companyName" label="" placeholder="Company's name" />
            </Grid>
          </Grid>
        </Stack>
      </FormSection>
    </>
  );
};

export default ChangeRequestForm;
